import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MOVEMENT_SOURCE_LABELS, type MouvementJour } from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { LibelleDae } from '../../components/LibelleDae';

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function Bloc({
  titre,
  mouvements,
  vide,
  libelleReference,
}: {
  titre: string;
  mouvements: MouvementJour[];
  vide: string;
  /**
   * Nom de la colonne d'identité de la ligne. Côté douane c'est **NUMCMM**, et
   * c'est le mot que KN emploie (O17) : « Référence » obligeait à traduire.
   */
  libelleReference: string;
}): ReactElement {
  return (
    <div className="detail-jour__bloc">
      <h4>
        {titre} <span className="compte">({mouvements.length})</span>
      </h4>
      {mouvements.length === 0 ? (
        <p className="detail-jour__vide">{vide}</p>
      ) : (
        <div className="detail-jour__tableau">
          <table className="tableau">
            <thead>
              <tr>
                {/* La référence commune ouvre le tableau : c'est elle qui
                    rapproche les deux côtés, les numéros internes ne servent
                    qu'à retrouver la pièce (règle 16). */}
                <th scope="col">Réf. BL/Appro</th>
                <th scope="col">Source</th>
                <th scope="col">{libelleReference}</th>
                <th scope="col">Support</th>
                <th scope="col">Motif</th>
                <th scope="col">Opération</th>
                <th scope="col">Sens</th>
                <th scope="col">UVC</th>
              </tr>
            </thead>
            <tbody>
              {mouvements.map((m, rang) => (
                <tr
                  key={`${m.source}-${m.reference ?? ''}-${rang}`}
                  className={m.rapprochement ? 'mouvement--rapproche' : undefined}
                  title={
                    m.rapprochement
                      ? `Rapproché à la main — ${m.rapprochement.typeLibelle}` +
                        ` · avec ${m.rapprochement.codpros.join(', ')}` +
                        ` · ${m.rapprochement.commentaire}`
                      : undefined
                  }
                >
                  <td
                    className={
                      m.apparie
                        ? `ref-bl ref-bl--apparie${m.apparieViaFamille ? ' ref-bl--via-famille' : ''}`
                        : 'ref-bl'
                    }
                  >
                    {m.refBl ?? '—'}
                    {m.apparie && !m.apparieViaFamille && (
                      <span
                        className="ref-bl__marque"
                        title="Cette référence se retrouve de l'autre côté, sous le même code, dans la fenêtre de recherche."
                      >
                        ✓
                      </span>
                    )}
                    {/*
                      Apparié **via la famille** : la contrepartie existe, mais
                      elle est déclarée sous un autre code. C'est une explication
                      légitime — la famille est faite pour ça — et elle ne se lit
                      pas comme un appariement direct. Le marqueur la distingue,
                      le code partenaire la nomme, et le lien y mène.
                    */}
                    {m.apparieViaFamille && (
                      <span className="marque-famille" title="Contrepartie déclarée sous un autre code de la famille validée.">
                        ✓ famille
                      </span>
                    )}
                    {m.apparieViaFamille && m.partenaire && (
                      <span className="via-famille">
                        contrepartie sous{' '}
                        <Link
                          className="lien-lie"
                          to={`/ecarts/${m.partenaire.codpro}`}
                          title={`Ouvrir la fiche de l'article ${m.partenaire.codpro}`}
                        >
                          ↗ {m.partenaire.codpro}
                        </Link>
                        {m.partenaire.designation && <> &middot; {m.partenaire.designation}</>}
                      </span>
                    )}
                  </td>
                  <td>
                    {MOVEMENT_SOURCE_LABELS[m.source]}
                    {m.rapprochement && (
                      <span className="etiquette etiquette--rapproche">
                        Rapproché · {m.rapprochement.typeLibelle}
                      </span>
                    )}
                    {/*
                      Les autres codes du rapprochement, **cliquables**.
                      Un rapprochement manuel relie souvent des mouvements
                      portes par des codes differents — c'est meme sa raison
                      d'etre. Lire « avec 433289 » sans pouvoir y aller oblige a
                      retenir le code, sortir de la fiche, le retaper.
                    */}
                    {m.rapprochement &&
                      m.rapprochement.codpros
                        .filter((code) => code !== m.codpro)
                        .map((code) => (
                          <Link
                            key={code}
                            className="lien-lie"
                            to={`/ecarts/${code}`}
                            title={`Ouvrir la fiche de l'article ${code}, relie par ce rapprochement`}
                          >
                            ↗ {code}
                          </Link>
                        ))}
                  </td>
                  <td>
                    <span>{m.reference ?? '—'}</span>
                    <LibelleDae
                      livraison={m.libelleDaeLivraison}
                      reception={m.libelleDaeReception}
                    />
                  </td>
                  <td>{m.support ?? '—'}</td>
                  <td>{m.motif ?? '—'}</td>
                  <td>{m.refOperation ?? '—'}</td>
                  <td>{m.sens ?? '—'}</td>
                  <td className="droite">{formaterNombre(m.uvc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Ce qui s'est passé un jour donné, sur les codes de l'écart (P4.1).
 *
 * Chargé **à la demande** : une fiche de famille couvre des centaines de
 * journées, et les précharger ferait payer à chaque ouverture un détail que
 * personne ne regardera.
 */
export function MouvementsDuJour({ id, jour }: { id: string; jour: string }): ReactElement {
  const detail = useQuery({
    queryKey: ['ecart', id, 'mouvements', jour],
    queryFn: () => api.mouvementsDuJour(id, jour),
    // Une journée passée ne change plus, sauf réimport : inutile de la
    // redemander à chaque dépliage.
    staleTime: 5 * 60 * 1000,
  });

  if (detail.isPending) return <p className="chargement">Chargement des mouvements…</p>;
  if (detail.isError) {
    return (
      <p className="alerte alerte--erreur" role="alert">
        {messageErreur(detail.error)}
      </p>
    );
  }

  return (
    <div className="detail-jour">
      <Bloc
        titre="WMS"
        mouvements={detail.data.wms}
        vide="Aucun mouvement physique ce jour-là."
        libelleReference="Référence"
      />
      <Bloc
        titre="Comptabilité matières"
        mouvements={detail.data.douane}
        vide="Aucune écriture déclarée ce jour-là."
        libelleReference="NUMCMM"
      />
    </div>
  );
}
